import React, { useEffect, useState } from 'react'
import { format, isValid } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { loadClassAlertsAsync } from '../../../server/server'
import { ReactComponent as TriangleWarningIcon } from '../../../assets/svg/TriangleWarningIcon.svg'
import InfoButton from '../../common/InfoButton'
import AnalyticsLoadingOverlay from '../AnalyticsLoadingOverlay'
import { AlertActivity, SingleEmotionDetails } from '../student/AlertDetails'
import { YearAlertSkeleton } from '../skeleton/YearAlertsSkeleton'

const ALERTS_PER_PAGE = 3

export default function YearAlertsWidget({ data, daysFilter }) {
    const { t } = useTranslation()
    return (
        <section aria-label={t('Alerts for selected classes')} className="bg-white rounded-[24px] p-5 mt-4" data-tour="alerts">
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple">{t('Alerts for selected classes')}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px]">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <InfoButton color="#5E639B" text={t('Unresolved alerts for this year during the selected period.')} />
            </div>
            <div className="flex flex-col gap-3 mt-4">
                <div className="flex items-center gap-1 pl-2 pb-3 border-b border-b-[#E8E0FF]">
                    <div className="w-7 h-7 shrink-0 rounded-full bg-[#FC4C5933] flex items-center justify-center" aria-hidden="true"><TriangleWarningIcon width={18} height={18} /></div>
                    <p className="text-[14px] leading-5 font-medium text-darkenPurple">
                        {t('Year total')} {' - '}<span className="text-[#FC4C59] font-semibold">{data.benchmark} {t(data.benchmark === 1 ? 'alert' : 'alerts')}</span>
                    </p>
                </div>
                {data.classes.map((classData) => <ClassAlerts key={classData.id} classData={classData} daysFilter={daysFilter} />)}
                {!data.classes.length && <p className="text-[14px] leading-5 text-mediumGray">{t('No alerts for this period')}</p>}
            </div>
        </section>
    )
}

function ClassAlerts({ classData, daysFilter }) {
    const { t } = useTranslation()
    const requestKey = JSON.stringify([classData.id, daysFilter.startDate, daysFilter.endDate])
    const [result, setResult] = useState({ key: null, items: [], error: false, visibleCount: ALERTS_PER_PAGE })
    const loading = result.key !== requestKey

    useEffect(() => {
        let active = true
        loadClassAlertsAsync(classData.id, daysFilter.startDate, daysFilter.endDate, 'all').then((response) => {
            if (!active) return
            if (response.status === 200 && Array.isArray(response.data)) {
                const items = response.data.filter((item) => !Object.prototype.hasOwnProperty.call(item, 'actionTakenBy') && !item.activityHistory?.some((activity) => activity.type === 'actionTaken'))
                setResult({ key: requestKey, items, error: false, visibleCount: ALERTS_PER_PAGE })
            } else setResult({ key: requestKey, items: [], error: true })
        }).catch(() => {
            if (active) setResult({ key: requestKey, items: [], error: true })
        })
        return () => { active = false }
    }, [classData.id, daysFilter.startDate, daysFilter.endDate, requestKey])

    const count = loading || result.error ? classData.alerts : result.items.length
    return (
        <section aria-label={classData.name} aria-busy={loading} className="bg-[#F8F8FB] rounded-[4px] p-2">
            <div className="flex items-center gap-1">
                <div className="w-7 h-7 shrink-0 rounded-full bg-[#FC4C5933] flex items-center justify-center" aria-hidden="true"><TriangleWarningIcon width={18} height={18} /></div>
                <p className="text-[14px] leading-5 font-medium text-darkenPurple">
                    {classData.name} {' - '}<span className="text-[#FC4C59] font-semibold">{count} {t(count === 1 ? 'alert' : 'alerts')}</span>
                </p>
            </div>
            <div className="relative mt-[11px]">
                {loading ? <>
                    <div aria-hidden="true" className="blur animate-pulse pointer-events-none select-none">
                        <YearAlertSkeleton />
                    </div>
                    <AnalyticsLoadingOverlay />
                </> : result.error ? <p role="alert" className="text-[14px] leading-5 text-[#78020B]">{t('Unable to load alerts for this class')}</p> : result.items.length ? (
                    <div className="flex flex-col gap-[6px]">
                        {result.items.slice(0, result.visibleCount).map((item) => <YearAlertItem key={item.id} item={item} />)}
                        {result.visibleCount < result.items.length && <button
                            type="button"
                            aria-label={`${t('Show more')} ${classData.name}`}
                            className="self-center mt-2 rounded-[8px] px-4 py-2 bg-[#F3EFFF] text-darkenPurple text-[14px] leading-5 font-semibold"
                            onClick={() => setResult((current) => ({ ...current, visibleCount: current.visibleCount + ALERTS_PER_PAGE }))}
                        >{t('Show more')}</button>}
                    </div>
                ) : <p className="text-[14px] leading-5 text-mediumGray">{t('No alerts for this period')}</p>}
            </div>
        </section>
    )
}

function YearAlertItem({ item }) {
    const { t } = useTranslation()
    const student = item.user ?? item.student
    const multipleEmotions = item.type === 'multipleNegativeEmotions'
    const date = new Date(item.created_date)
    const assignment = item.activityHistory?.filter((activity) => activity.type === 'assignedAction').slice(-1)[0]?.assignedTo
    return (
        <article className="bg-white rounded-[8px] p-3 border border-[#FC4C5933]">
            <div className="flex flex-wrap items-center gap-2">
                <div className="w-7 h-7 shrink-0 rounded-full bg-[#FC4C5933] flex items-center justify-center" aria-hidden="true"><TriangleWarningIcon width={18} height={18} /></div>
                <p className="text-[14px] leading-5 font-semibold text-darkenPurple break-words">{student?.first_name} {student?.last_name}</p>
                <p className="text-[14px] leading-5 font-semibold text-[#FC4C59]">{t(multipleEmotions ? 'Multiple Uncomfortable Emotions' : item.category_name || 'Keyword Alert')}</p>
                {isValid(date) && <time dateTime={date.toISOString()} className="text-[12px] leading-5 font-medium text-mediumGray">{format(date, 'dd.MM.yyyy HH:mm')}</time>}
                <span className={`text-[12px] leading-5 font-medium rounded-full px-[10px] py-1 ${assignment ? 'bg-[#ECECF2] text-mediumGray' : 'bg-[#FC4C5933] text-[#78020B]'}`}>
                    {assignment ? `${t('Assigned to')} ${assignment.first_name ?? ''} ${assignment.last_name ?? ''}` : t('Not assigned')}
                </span>
            </div>
            {!multipleEmotions && item.feedback?.message && <p className="bg-[#FFF5F6] rounded-[8px] p-3 mt-3 text-darkenPurple text-[14px] leading-[22px] font-medium break-words whitespace-pre-wrap">{item.feedback.message}</p>}
            {!!item.feedbacks?.length && <div className="flex flex-col gap-[10px] mt-3">
                {item.feedbacks.map((emotion) => <SingleEmotionDetails key={emotion.id} emotion={emotion} />)}
            </div>}
            {!!item.activityHistory?.length && <div className="border-t border-t-[#ECEBF8] mt-3 pt-3">
                {item.activityHistory.map((activity, index) => <AlertActivity key={activity.id ?? index} activity={activity} />)}
            </div>}
        </article>
    )
}
